import { observable, flow, action } from "mobx";
import { getCampaign, pauseCampaign, resumeCampaign } from "services/campaigns";
import { message, notification } from "antd";
import { registerHandler, delay } from "services/api";
import { addProject, deleteProjects, renameProject, duplicateProject } from "services/project";
import {
	addAccountProfile,
	renameAccountProfile,
	deleteAccountProfiles
} from "services/accountProfile";
import { addLinkSet, renameLinkSet, deleteLinkSets } from "services/linkSet";
import { saveAccountCreationProject } from "../services/api";

export default class CampaignStore {
	@observable
	projects = [];
	@observable
	accountProfiles = [];
	@observable
	urlLists = [];
	@observable
	campaign = {};
	@observable
	ui = {};

	constructor() {
		this.ui = this.initUI();
	}

	initUI = () => {
		const ui = {
			changingCampaignStatus: false,
			loadingCampaign: false,
			selectedProjectKeys: [],
			addEditProjectDialogVisible: false,
			duplicateProjectDialogVisible: false,
			lastProjectAction: null,
			addingProject: false,
			renamingProject: false,
			currentProject: null,
			deletingProjects: false,
			selectedAccountProfileKeys: [],
			currentAccountProfile: null,
			addEditAccountProfileDialogVisible: false,
			addingAccountProfile: false,
			renamingAccountProfile: false,
			deletingAccountProfiles: false,

			selectedLinkSetKeys: [],
			currentLinkSet: null,
			addEditLinkSetDialogVisible: false,
			addingLinkSet: false,
			renamingLinkSet: false,
			deletingLinkSets: false,			
		};

		return observable.object(ui);
	};

	//Project
	@action
	selectProjects(selectedRowKeys) {
		this.ui.selectedProjectKeys = observable.array(selectedRowKeys);
	}

	@action
	addProject = project => {
		this.projects.push(project);
	};

	@action
	showAddProjectDialog = () => {
		this.ui.addEditProjectDialogVisible = true;
		this.ui.currentProject = null;
		this.ui.lastProjectAction = "add";
	};

	@action
	showRenameProjectDialog = payload => {
		this.ui.addEditProjectDialogVisible = true;
		this.ui.currentProject = observable.object(payload);
		this.ui.lastProjectAction = "rename";
	};

	@action
	showDuplicateProjectDialog = payload => {
		this.ui.duplicateProjectDialogVisible = true;				
		this.ui.currentProject = observable.object(payload);
		this.ui.lastProjectAction = "duplicate";
	};

	@action
	hideDuplicateProjectDialog = () => {
		this.ui.duplicateProjectDialogVisible = false;
	};

	@action
	hideAddEditProjectDialog = () => {
		this.ui.addEditProjectDialogVisible = false;
	};

	@action
	deleteProjects = ids => {
		for (let id of ids) {
			let foundIndex = this.projects.findIndex(item => item.id == id);
			if (foundIndex != -1) {
				this.projects.splice(foundIndex, 1);
			}
		}
	};

	@action
	renameProject = ({ id, name }) => {
		let foundIndex = this.projects.findIndex(item => item.id == id);
		if (foundIndex != -1) {
			this.projects[foundIndex].name = name;
		}
	};
	
	pauseResumeCampaign = flow(function*() {
		let response = null;
		this.ui.changingCampaignStatus = true;
		yield delay(1000);
		if (this.campaign.campaignStatus == "ACTIVE") {
			response = yield pauseCampaign(this.campaign.id);
		} else {
			response = yield resumeCampaign(this.campaign.id);
		}
		this.ui.changingCampaignStatus = false;
		if (response == null) {
			message.error("Failed to pause/resume the campaign. Unknown error", 5);
			return;			
		}		

		if (response.error) {
			message.error(response.error, 5);
			return;
		}

		message.success("Successfully pause/resume the campaign");
	});

	@action
	changeCampaignStatus = (status) => {
		this.campaign.campaignStatus = status;
	}

	remoteDeleteProjects = flow(function*() {
		this.ui.deletingProjects = true;

		const response = yield deleteProjects({
			campaignId: this.campaign.id,
			ids: this.ui.selectedProjectKeys
		});

		this.ui.deletingProjects = false;
		this.ui.selectedProjectKeys = [];

		if (response == null) {
			message.error("Failed to delete the selected projects. Unknown error", 5);
			return;
		}

		if (response.error) {
			message.error(response.error.message, 5);
			return;
		}

		message.success("Successfully deleted the selected projects", 5);
		return;
	});

	remoteAddProject = flow(function*(payload) {
		this.ui.addingProject = true;
		this.ui.currentProject = payload;
		const newProjectPayload = { ...payload, campaignId: this.campaign.id };
		const response = yield addProject(newProjectPayload);
		this.ui.addingProject = false;

		if (response == null) {
			message.error("Failed to add a new project. Unknown error.", 5);
			return;
		}

		if (response.error) {
			message.error(response.error.message, 5);
			return;
		}

		message.success("Successfully added a new project", 5);
		this.ui.addEditProjectDialogVisible = false;
		this.ui.currentProject = null;
	});

	remoteRenameProject = flow(function*(name) {
		this.ui.renamingProject = true;
		this.ui.currentProject.name = name;

		const renamedProjectPayload = {
			campaignId: this.campaign.id,
			module: this.ui.currentProject.module,
			name: this.ui.currentProject.name
		};

		const response = yield renameProject(
			this.ui.currentProject.id,
			renamedProjectPayload
		);
		this.ui.renamingProject = false;

		if (response == null) {
			message.error("Failed to rename the project. Unknown error", 5);
			return;
		}

		if (response.error) {
			message.error(response.error.message, 5);
			return;
		}

		message.success("Successfully renamed the project", 5);
		this.ui.addEditProjectDialogVisible = false;
		this.ui.currentProject = null;
		return;
	});

	remoteDuplicateProject = flow(function*(name) {
		this.ui.duplicatingProject = true;
		this.ui.currentProject.name = name;

		const duplicatedProjectPayload = {			
			name: this.ui.currentProject.name
		};

		const response = yield duplicateProject(
			this.ui.currentProject.id,
			duplicatedProjectPayload
		);

		this.ui.duplicatingProject = false;

		if (response == null) {
			message.error("Failed to duplicate the project. Unknown error", 5);
			return;
		}

		if (response.error) {
			message.error(response.error.message, 5);
			return;
		}

		message.success("Successfully duplicated the project", 5);

		this.ui.duplicateProjectDialogVisible = false;
		this.ui.currentProject = null;
		return;
	});

	//End Project

	//Account Profile

	@action
	selectAccountProfiles(selectedRowKeys) {
		this.ui.selectedAccountProfileKeys = observable.array(selectedRowKeys);
	}

	@action
	showAddAccountProfileDialog = () => {
		this.ui.addEditAccountProfileDialogVisible = true;
		this.ui.currentAccountProfile = null;
	};

	@action
	showRenameAccountProfileDialog = payload => {
		this.ui.addEditAccountProfileDialogVisible = true;
		this.ui.currentAccountProfile = observable.object(payload);
	};

	@action
	hideAddEditAccountProfileDialog = () => {
		this.ui.addEditAccountProfileDialogVisible = false;
	};

	@action
	deleteAccountProfiles = ids => {
		for (let id of ids) {
			let foundIndex = this.accountProfiles.findIndex(item => item.id == id);
			if (foundIndex != -1) {
				this.accountProfiles.splice(foundIndex, 1);
			}
		}
	};

	@action
	renameAccountProfile = ({ id, name }) => {
		const foundIndex = this.accountProfiles.findIndex(item => item.id == id);
		if (foundIndex != -1) {
			this.accountProfiles[foundIndex].name = name;
		}
	};

	@action
	addAccountProfile = profile => {
		this.accountProfiles.push(profile);
	};

	remoteAddAccountProfile = flow(function*(name) {
		this.ui.addingAccountProfile = true;
		const payload = {
			campaignId: this.campaign.id,
			name: name
		};
		this.ui.currentAccountProfile = payload;
		const response = yield addAccountProfile(payload);

		this.ui.addingAccountProfile = false;

		if (response == null) {
			message.error("Failed to add new account profile. Unknown error", 5);
			return;
		}

		if (response.error) {
			message.error(response.error.message, 5);
			return;
		}

		message.success("Successfully added new account profile", 5);
		this.ui.addEditAccountProfileDialogVisible = false;
		this.ui.currentAccountProfile = null;
		return;
	});

	remoteDeleteAccountProfiles = flow(function*() {
		this.ui.deletingAccountProfiles = true;

		const response = yield deleteAccountProfiles({
			campaignId: this.campaign.id,
			ids: this.ui.selectedAccountProfileKeys
		});

		this.ui.deletingAccountProfiles = false;
		this.ui.selectedAccountProfileKeys = [];

		if (response == null) {
			message.error(
				"Failed to delete the selected account profiles. Unknown error",
				5
			);
			return;
		}

		if (response.error) {
			message.error(response.error.message, 5);
			return;
		}

		message.success("Successfully deleted the selected account profiles", 5);
		return;
	});

	remoteRenameAccountProfile = flow(function*(name) {
		this.ui.renamingAccountProfile = true;
		this.ui.currentAccountProfile.name = name;

		const renamedAccountProfilePayload = {
			campaignId: this.campaign.id,
			id: this.ui.currentAccountProfile.id,
			name: this.ui.currentAccountProfile.name
		};

		const response = yield renameAccountProfile(renamedAccountProfilePayload);

		this.ui.renamingAccountProfile = false;

		if (response == null) {
			message.error("Failed to rename the account profile. Unknown error", 5);
			return;
		}

		if (response.error) {
			message.error(response.error.message, 5);
			return;
		}

		message.success("Successfully renamed the account profile", 5);
		this.ui.addEditAccountProfileDialogVisible = false;
		this.ui.currentAccountProfile = null;
		return;
	});

	//End Account Profile

	//LinkSet
	@action
	selectLinkSets(selectedRowKeys) {
		this.ui.selectedLinkSetKeys = observable.array(selectedRowKeys);
	}

	@action
	showAddLinkSetDialog = () => {
		this.ui.addEditLinkSetDialogVisible = true;
		this.ui.currentLinkSet = null;
	};

	@action
	showRenameLinkSetDialog = payload => {
		this.ui.addEditLinkSetDialogVisible = true;
		this.ui.currentLinkSet = observable.object(payload);
	};

	@action
	hideAddEditLinkSetDialog = () => {
		this.ui.addEditLinkSetDialogVisible = false;
	};

	@action
	addLinkSet = linkset => {
		this.urlLists.push(linkset);
	};

	@action
	renameLinkSet = ({ id, name }) => {
		const foundIndex = this.urlLists.findIndex(item => item.id == id);
		if (foundIndex != -1) {
			this.urlLists[foundIndex].name = name;
		}
	};

	@action
	deleteLinkSets = ids => {
		for (let id of ids) {
			let foundIndex = this.urlLists.findIndex(item => item.id == id);
			if (foundIndex != -1) {
				this.urlLists.splice(foundIndex, 1);
			}
		}
	};

	remoteAddLinkSet = flow(function*(name) {
		this.ui.addingLinkSet = true;
		const payload = {
			campaignId: this.campaign.id,
			name: name
		};
		this.ui.currentLinkSet = payload;
		const response = yield addLinkSet(payload);

		this.ui.addingLinkSet = false;

		if (response == null) {
			message.error("Failed to add new link set. Unknown error", 5);
			return;
		}

		if (response.error) {
			message.error(response.error.message, 5);
			return;
		}

		message.success("Successfully added new link set", 5);
		this.ui.addEditLinkSetDialogVisible = false;
		this.ui.currentLinkSet = null;
		return;
	});

	remoteRenameLinkSet = flow(function*(name) {
		this.ui.renamingLinkSet = true;
		this.ui.currentLinkSet.name = name;

		const renamedLinkSetPayload = {
			campaignId: this.campaign.id,
			id: this.ui.currentLinkSet.id,
			name: this.ui.currentLinkSet.name
		};

		const response = yield renameLinkSet(renamedLinkSetPayload);

		this.ui.renamingLinkSet = false;

		if (response == null) {
			message.error("Failed to rename the link set. Unknown error", 5);
			return;
		}

		if (response.error) {
			message.error(response.error.message, 5);
			return;
		}

		message.success("Successfully renamed the link set", 5);
		this.ui.addEditLinkSetDialogVisible = false;
		this.ui.currentLinkSet = null;
		return;
	});

	remoteDeleteLinkSets = flow(function*() {
		this.ui.deletingLinkSets = true;

		const response = yield deleteLinkSets({
			campaignId: this.campaign.id,
			ids: this.ui.selectedLinkSetKeys
		});

		this.ui.deletingLinkSets = false;
		this.ui.selectedLinkSetKeys = [];

		if (response == null) {
			message.error(
				"Failed to delete the selected link sets. Unknown error",
				5
			);
			return;
		}

		if (response.error) {
			message.error(response.error.message, 5);
			return;
		}

		message.success("Successfully deleted the selected link sets", 5);
		return;
	});
	
	//End LinkSet

	@action
	changeProjectStatus = (projectId, status) => {
		this.projects.map(project => {
			if (project.id == projectId) {
				project.status = status
			}
		});
	}

	fetchCampaign = flow(function*(campaignId) {
		this.ui.loadingCampaign = true;
		const response = yield getCampaign(campaignId);
		this.ui = this.initUI();

		if (response == null) {
			message.error("Failed to get the campaign. Unknown error", 5);
			return;
		}

		if (response.error) {
			notification.error({
				message: "Failed to get the campaign",
				description: response.error.message
			});
			return;
		}

		this.projects = observable.array(response.projects);
		this.accountProfiles = observable.array(response.profiles);
		this.urlLists = observable.array(response.urlLists);
		this.campaign = observable.object(response.campaign);

		registerHandler([
			{
				path: "campaign/" + campaignId,
				handler: (err, msg) => {
					if (err) {
						return;
					}
					var obj = JSON.parse(msg.body);
					if (obj.action === "campaignStatusChanged") {
						this.changeCampaignStatus(obj.status);
						return;
					}

					if (obj.action == "projectStatusChanged") {
						this.changeProjectStatus(obj.projectId, obj.status);
					}

					if (obj.action === "newProject") {
						this.addProject(obj.project);
						return;
					}
					if (obj.action == "projectsDeleted") {
						this.deleteProjects(obj.projects);
						return;
					}
					if (obj.action == "projectEdited") {
						this.renameProject({
							id: obj.project.id,
							name: obj.project.name
						});
						return;
					}

					if (obj.action == "newProfile") {
						this.addAccountProfile({ ...obj.profile, count: 0 });
						return;
					}

					if (obj.action == "profileEdited") {
						this.renameAccountProfile({
							id: obj.id,
							name: obj.name
						});
						return;
					}

					if (obj.action == "profilesDeleted") {
						this.deleteAccountProfiles(obj.profiles);
						return;
					}

					if (obj.action == "newUrlList") {
						this.addLinkSet({ ...obj.urlList, count: 0 });
						return;
					}

					if (obj.action == "urlListEdited") {
						this.renameLinkSet({
							id: obj.id,
							name: obj.name
						});
						return;
					}

					if (obj.action == "urlListsDeleted") {
						this.deleteLinkSets(obj.urlList);
						return;
					}
				}
			}
		]);
	});
}
